import { useCallback, type MutableRefObject, type Dispatch, type SetStateAction } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { getERPClient } from "@/lib/erp/client";
import { prepareDocForSave } from "@/lib/erp/save";
import { pruneLocalPlaceholderChildRowsInDoc } from "@/components/child-table/utils/prune";
import { itemQueryKeys } from "../../api/itemQueryKeys";
import { routes } from "@/lib/config/routes";
import type { DocTypeSchema } from "@/lib/erp/meta";
import type { VariantDraft } from "@/lib/erp/variants";
import { logger } from "@/lib/utils/logger";
import { toast } from "sonner";
import { parseInsertError } from "../utils/parseInsertError";

interface ItemSaveDeps {
  isNew: boolean;
  itemName: string;
  formData: Record<string, any>;
  setFormData: Dispatch<SetStateAction<Record<string, any>>>;
  setOriginalData: Dispatch<SetStateAction<Record<string, any>>>;
  setDocVersion: Dispatch<SetStateAction<number>>;
  isSavingRef: MutableRefObject<boolean>;
  metaSchema: DocTypeSchema | undefined;
  saveMutation: { mutateAsync: (data: any) => Promise<any> };
  createMutation: { mutateAsync: (data: any) => Promise<any> };
  validateForm: (data?: Record<string, any>) => Promise<{ valid: boolean; errors: string[] }>;
  maybeCreateVariantsAfterSave: (
    savedItem: Record<string, any>,
    draft: VariantDraft,
    isTemplate: boolean,
    selectedIds?: Set<string>,
    skuOverrides?: Record<string, string>,
  ) => Promise<void>;
  /** Staged media context (only available in create mode) */
  stagedMediaContext?: {
    stagedMedia: Array<{ file_url: string; file_name: string; is_primary_image?: boolean; localPreviewUrl?: string }>;
    setStagedMedia: (media: any[]) => void;
  } | null;
  /** Variant draft from context */
  variantDraft?: VariantDraft | null;
  variantDraftContext?: { skuOverridesByKey?: Record<string, string> } | null;
  selectedVariantIds?: Set<string>;
  /** Set field-level errors from server insert failure (Epic 02, Plan 166) */
  setFieldErrors?: (errors: Record<string, string>) => void;
}

/**
 * Hook that provides save handlers for the Item detail page.
 *
 * Returns:
 * - handleSave: Full save (create or update)
 * - handleSaveWithPatch: Patch-based save from modal submissions
 * - handleModalSaveSuccess: Sync state after direct modal saves
 */
export function useItemSave(deps: ItemSaveDeps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    isNew,
    itemName,
    formData,
    setFormData,
    setOriginalData,
    setDocVersion,
    isSavingRef,
    metaSchema,
    saveMutation,
    createMutation,
    validateForm,
    maybeCreateVariantsAfterSave,
    stagedMediaContext,
    variantDraft,
    variantDraftContext,
    selectedVariantIds,
    setFieldErrors,
  } = deps;

  /**
   * Full save — handles both create and update flows.
   * @param dataOverride Optional data to use instead of current formData
   */
  const handleSave = async (dataOverride?: Record<string, any>) => {
    const dataToSave = dataOverride ?? formData;

    const validation = await validateForm(dataToSave);
    if (!validation.valid) {
      alert(`Please fix validation errors:\n${validation.errors.join("\n")}`);
      throw new Error(`Validation failed: ${validation.errors.join(", ")}`);
    }

    const docToSaveRaw = prepareDocForSave(dataToSave, metaSchema!);

    // Capture bundle mode flag before stripping it from the doc
    const shouldCreateBundle = isNew && docToSaveRaw.__bundle_mode === 1;
    delete docToSaveRaw.__bundle_mode;

    // Auto-generate item_code from item_name for new items
    if (isNew && !docToSaveRaw.item_code && docToSaveRaw.item_name) {
      docToSaveRaw.item_code = docToSaveRaw.item_name;
    }

    // Remove local placeholder rows before save
    const docToSave = pruneLocalPlaceholderChildRowsInDoc(
      docToSaveRaw,
      metaSchema!,
      (doctype) => queryClient.getQueryData<DocTypeSchema>(["meta", doctype]),
    );

    isSavingRef.current = true;

    try {
      let updatedItem;

      if (isNew) {
        // Clear any previous field errors before attempting insert
        setFieldErrors?.({});

        try {
          updatedItem = await createMutation.mutateAsync(docToSave);
        } catch (insertError: unknown) {
          // Parse server error into field-level map + toast message
          const fieldNames = metaSchema?.fields.map((f) => f.fieldname) ?? [];
          const parsed = parseInsertError(insertError, fieldNames);

          // Set field errors for inline error rings
          if (Object.keys(parsed.fieldErrors).length > 0) {
            setFieldErrors?.(parsed.fieldErrors);

            // Auto-scroll to first errored field
            const firstField = Object.keys(parsed.fieldErrors)[0];
            requestAnimationFrame(() => {
              const el = document.querySelector(`[data-inline-field="${firstField}"]`);
              if (el) {
                el.scrollIntoView({ block: "center", behavior: "smooth" });
                // Focus the row after scroll completes
                setTimeout(() => (el as HTMLElement).focus?.(), 300);
              }
            });
          }

          toast.error(parsed.toastMessage, { duration: 6000 });
          logger.error("[useItemSave] Insert failed:", insertError);
          return; // Do NOT re-throw — caller has no .catch()
        }

        const newItemName = updatedItem.name || updatedItem.item_code;

        // Attach staged media to the new item
        await attachStagedMedia(newItemName, stagedMediaContext);

        // Create variants if template mode — wrapped in its own try/catch
        // so variant failure doesn't block navigation to the saved item
        const isTemplate = docToSave.has_variants === 1;
        if (isTemplate && variantDraft && variantDraft.attributes.length > 0) {
          try {
            const skuOverrides = variantDraftContext?.skuOverridesByKey;
            await maybeCreateVariantsAfterSave(
              updatedItem,
              variantDraft,
              isTemplate,
              selectedVariantIds,
              skuOverrides,
            );
          } catch (variantError) {
            logger.error("[useItemSave] Variant creation failed (item saved successfully):", variantError);
          }
        }

        // Create Product Bundle if bundle mode was toggled on
        if (shouldCreateBundle && newItemName) {
          try {
            await getERPClient().call("frappe.client.insert", {
              doc: {
                doctype: "Product Bundle",
                new_item_code: newItemName,
                items: [],
              },
            });
          } catch (bundleError) {
            logger.error("[useItemSave] Bundle creation failed (item saved successfully):", bundleError);
          }
        }

        // Navigate to the new item — always navigate if item was created
        if (newItemName) {
          toast.success("Item created");
          queryClient.invalidateQueries({
            queryKey: ["doc-sidebar", "Item", newItemName],
          });
          navigate(routes.item.detail(newItemName));
          return;
        }
      } else {
        updatedItem = await saveMutation.mutateAsync(docToSave);

        // Create variants if template mode (for existing items)
        const isTemplate = updatedItem.has_variants === 1;
        if (isTemplate && variantDraft && variantDraft.attributes.length > 0) {
          const skuOverrides = variantDraftContext?.skuOverridesByKey;
          await maybeCreateVariantsAfterSave(
            updatedItem,
            variantDraft,
            isTemplate,
            selectedVariantIds,
            skuOverrides,
          );
        }
      }

      // Update local state with server response
      setFormData(updatedItem);
      setOriginalData(updatedItem);
      setDocVersion((v) => v + 1);

      const finalItemName = updatedItem.name || updatedItem.item_code;
      if (finalItemName) {
        queryClient.setQueryData(
          itemQueryKeys.detail(finalItemName),
          updatedItem,
        );
      }
    } finally {
      isSavingRef.current = false;
    }
  };

  /**
   * Patch-based save — used by modal submissions.
   * Applies optimistic UI update, validates, saves, reverts on failure.
   */
  const handleSaveWithPatch = async (patch: Record<string, any>) => {
    if (Object.keys(patch).length === 0) return;

    const mergedData = { ...formData, ...patch };

    // Optimistic UI update
    setFormData(mergedData);
    isSavingRef.current = true;

    try {
      const validation = await validateForm(mergedData);
      if (!validation.valid) {
        setFormData(formData);
        throw new Error(
          `Validation failed: ${validation.errors.join(", ")}`,
        );
      }

      const updatedItem = await saveMutation.mutateAsync({
        patch,
        currentData: mergedData,
      });

      setFormData(updatedItem);
      setOriginalData(updatedItem);
      setDocVersion((v) => v + 1);
      queryClient.setQueryData(
        itemQueryKeys.detail(itemName),
        updatedItem,
      );
    } catch (error) {
      logger.error(
        "[handleSaveWithPatch] Save failed, reverting optimistic update:",
        error,
      );
      throw error;
    } finally {
      isSavingRef.current = false;
    }
  };

  /**
   * Sync state after a direct modal save (e.g. MetaFieldEditModal).
   */
  const handleModalSaveSuccess = useCallback(
    (updatedDoc: Record<string, any>) => {
      setFormData(updatedDoc);
      setOriginalData(updatedDoc);
      setDocVersion((v) => v + 1);
      queryClient.setQueryData(
        itemQueryKeys.detail(itemName),
        updatedDoc,
      );
    },
    [itemName, queryClient, setFormData, setOriginalData, setDocVersion],
  );

  return { handleSave, handleSaveWithPatch, handleModalSaveSuccess };
}

/**
 * Attach staged media (images/videos) to a newly created item.
 * Handles file attachment, gallery ordering, primary image, and cleanup.
 */
async function attachStagedMedia(
  newItemName: string | undefined,
  stagedMediaContext: ItemSaveDeps["stagedMediaContext"],
) {
  const stagedMedia = stagedMediaContext?.stagedMedia || [];
  if (stagedMedia.length === 0 || !newItemName) return;

  try {
    // Step 1: Attach files
    const files = stagedMedia.map((img) => ({
      file_url: img.file_url,
      file_name: img.file_name,
    }));

    await getERPClient().call(
      "zaviago_backend.api.item_gallery.add_images_from_files",
      {
        item_code: newItemName,
        files_json: JSON.stringify(files),
      },
    );

    // Step 2: Save gallery with primary + order
    const primaryIndex = stagedMedia.findIndex(
      (img) => img.is_primary_image === true,
    );
    const galleryData = stagedMedia.map((img, index) => ({
      image: img.file_url,
      alt_text: img.file_name || "",
      media_type: "image" as const,
      is_primary_image:
        index === primaryIndex || (primaryIndex === -1 && index === 0)
          ? 1
          : 0,
      is_primary_video: 0,
    }));

    try {
      await getERPClient().call(
        "zaviago_backend.api.item_gallery.save_item_gallery",
        {
          item_code: newItemName,
          gallery_json: JSON.stringify(galleryData),
        },
      );
    } catch (saveError) {
      logger.error(
        "[useItemSave] Failed to save gallery order/primary:",
        saveError,
      );
    }
  } catch (error) {
    logger.error(
      "[useItemSave] Failed to attach images after create:",
      error,
    );
  }

  // Cleanup blob URLs
  stagedMedia.forEach((img) => {
    if (img.localPreviewUrl) {
      URL.revokeObjectURL(img.localPreviewUrl);
    }
  });

  // Clear context
  if (stagedMediaContext) {
    stagedMediaContext.setStagedMedia([]);
  } else if (import.meta.env.DEV) {
    logger.warn(
      "[useItemSave] ItemCreateMediaContext missing during image cleanup",
    );
  }
}
